import {getAppTypeHeader, getBaseFetchHeaders} from '@github-ui/fetch-headers'
import type {Location} from '@github-ui/react-router'
import {addRequestId} from '@github-ui/recent-request-ids'
import type React from 'react'
import {Suspense} from 'react'

import type {ChildRoute, LoaderResult, NavigatorRouteRegistration, TransitionType} from './app-routing-types'

export interface JsonRouteProps {
  path: string
  Component: React.ComponentType & {
    preload?: () => Promise<{default: React.ComponentType}>
  }
  transitionType?: TransitionType
  shouldNavigateOnError?: boolean
  children?: ChildRoute[]
  fallback?: React.ReactNode | null
}
/**
 * A utility for building a route registration for a route that consumes a JSON object (either via embedding in a
 * script tag or via a JSON fetch on React soft navigations).
 *
 * @deprecated This API is deprecated and is no longer supported. Consider migrating to DataRouter instead (https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router).
 */
export function jsonRoute<TRouteData, TEmbeddedData>({
  path,
  Component,
  /**
   * Some routes want to handle their own errors and not just show a generic error page. In this case,
   * the error is just handled like any other payload for the route.
   * NOTE: We might consider replacing the shouldNavigateOnError flag with a parallel implementation to jsonRoute.
   */
  shouldNavigateOnError,
  transitionType,
  children,
  fallback = null,
}: JsonRouteProps): NavigatorRouteRegistration<TRouteData, TEmbeddedData> {
  async function coreLoader({location}: {location: Location}): Promise<LoaderResult<TRouteData>> {
    let response: Response
    try {
      const fetchPath = `${location.pathname}${location.search}`
      response = await window.fetch(fetchPath, {
        headers: {
          Accept: 'application/json',
          ...getBaseFetchHeaders(),
          'X-GitHub-Target': 'dotcom',
          'X-React-Router': 'json',
          ...getAppTypeHeader('navigator'),
        },
      })
      const requestId = response?.headers?.get('X-Github-Request-Id')
      if (requestId) addRequestId(requestId)
    } catch {
      return {
        type: shouldNavigateOnError ? 'route-handled-error' : 'error',
        error: {type: 'fetchError'},
      }
    }
    if (response.redirected) {
      return {
        type: 'redirect',
        url: response.url,
      }
    } else if (!response.ok) {
      return {
        type: shouldNavigateOnError ? 'route-handled-error' : 'error',
        error: {type: 'httpError', httpStatus: response.status},
      }
    } else {
      try {
        const responseJson = await response.json()
        return {
          type: 'loaded',
          data: responseJson,
          title: responseJson.title,
        }
      } catch {
        return {
          type: shouldNavigateOnError ? 'route-handled-error' : 'error',
          error: {type: 'badResponseError'},
        }
      }
    }
  }

  function loadFromEmbeddedData({embeddedData}: {embeddedData: TEmbeddedData}): {data: TRouteData; title: string} {
    const data = embeddedData as unknown as TRouteData & {title: string}
    return {data, title: data.title}
  }

  const JsonRouteWrapper: React.FC = () => {
    return (
      <Suspense fallback={fallback}>
        <Component />
      </Suspense>
    )
  }

  return {
    path,
    // TODO: consider wrapping Component with a component that passes payload as a prop — ideally as part of migrating
    // away from useRoutePayload.
    Component: Object.assign(JsonRouteWrapper, {preload: Component.preload}),
    coreLoader,
    loadFromEmbeddedData,
    transitionType,
    children,
  }
}
